import type { LocalProject } from "@/context/layout"
import { useLanguage } from "@/context/language"
import { ServerConnection } from "@/context/server"
import {
  createProject,
  createPeriod,
  createSession,
  deleteDatoxSession,
  deletePeriod,
  deleteProject,
  type DatoxProject,
  type DatoxPeriod,
  type DatoxSession,
  ensureRemoteSession,
  listProjects,
  listPeriods,
  listSessions,
  updateProject,
  listCompanies,
} from "@/datox/api"
import { useServer } from "@/context/server"
import { sessionHref } from "@/utils/session-route"
import { localDevHeaders } from "@/datox/local-auth"
import { useDatoxAuth } from "@/datox/auth"
import { ScrollView } from "@opencode-ai/ui/scroll-view"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import type { Session } from "@opencode-ai/sdk/v2/client"
import { useNavigate } from "@solidjs/router"
import { useSettingsCommand } from "@/components/settings-dialog"
import { showToast } from "@/utils/toast"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { TooltipV2 } from "@opencode-ai/ui/v2/tooltip-v2"
import { DateTime } from "luxon"
import { createEffect, createMemo, createResource, For, onCleanup, onMount, Show, type JSX } from "solid-js"
import { createStore } from "solid-js/store"
import { HomeProjectsView } from "./home-projects-view"
import { HomeSessionsView } from "./home-sessions-view"
import { createHomeScrollController } from "./home-scroll-controller"
import {
  DialogDatoxDeleteProject,
  DialogDatoxDeleteSession,
  DialogDatoxLogout,
  DialogDatoxPeriodForm,
  DialogDatoxProjectForm,
} from "./datox-project-dialogs"
import type { HomeSessionGroup, HomeSessionRecord, OpenSessionOptions } from "./home-sessions-controller"

const datoxServer: ServerConnection.Http = {
  type: "http",
  http: { url: "datox-agent-api" },
  displayName: "Datox Agent API",
}

type NavigationAgent = {
  id: string
  name: string
  description?: string
  instructions?: string
  mode?: string
  enabled?: boolean
  isDefault?: boolean
  tools?: Record<string, boolean>
  integrations?: string[]
  permissions?: Record<string, string>
  updatedAt?: string
}

export function DatoxHome() {
  const language = datoxLanguage(useLanguage())
  const dialog = useDialog()
  const openSettings = useSettingsCommand()
  const navigate = useNavigate()
  const server = useServer()
  const auth = useDatoxAuth()
  const [store, setStore] = createStore({
    selectedProjectID: storedSelectedProject(),
    selectedSessionID: "",
    selectedPeriodID: storedSelectedPeriod(),
    periods: [] as DatoxPeriod[],
    sessions: [] as DatoxSession[],
    search: "",
    searchFocused: false,
    defaultProjectSelected: false,
    navigationCollapsed: storedNavigationCollapsed(),
    centralView: storedCentralView(),
    selectedAgent: storedSelectedAgent(),
  })
  const [projectsResource, projectActions] = createResource(() => (auth.authenticated() ? true : undefined), listProjects)
  const [companies] = createResource(() => (auth.authenticated() ? true : undefined), listCompanies)
  const projects = createMemo(() => projectsResource() ?? [])
  const localProjects = createMemo(() => projects().map(toLocalProject))
  const selectedProject = createMemo(() => projects().find((project) => project.id === store.selectedProjectID))
  const selectedLocalProject = createMemo(() => localProjects().find((project) => project.worktree === store.selectedProjectID))
  const records = createMemo(() => buildRecords(store.sessions, localProjects()))
  const groups = createMemo(() => groupSessions(records(), language))
  const filteredRecords = createMemo(() => {
    const query = store.search.trim().toLowerCase()
    if (!query) return []
    return records().filter((record) => `${record.session.title} ${record.projectName}`.toLowerCase().includes(query))
  })
  const scroll = createHomeScrollController(groups)
  const companyLabel = () => {
    const company = companies()?.find((item) => item.id === localDevHeaders()["X-Active-Company-Id"])
    if (company?.name === "Local Development Company") return language.t("datox.company.default")
    return company?.name ?? language.t("datox.company.default")
  }
  const profileLabel = companyLabel
  const profileDescription = () => auth.state.accountName || auth.state.accountEmail || localDevHeaders()["X-User-Email"] || localDevHeaders()["X-User-Id"]

  createEffect(() => {
    if (store.defaultProjectSelected) return
    const stored = storedSelectedProject()
    const project = projects().find((item) => item.id === stored) ?? latestProject(projects())
    if (!project) return
    setStore("defaultProjectSelected", true)
    selectProject(project.id)
  })

  onMount(() => {
    setTimeout(applyStoredNavigationSelection)
    const navigationListener = (event: Event) => {
      const detail = (event as CustomEvent<{ collapsed?: boolean }>).detail
      setStore("navigationCollapsed", detail?.collapsed ?? storedNavigationCollapsed())
    }
    const selectionListener = (event: Event) => {
      const detail = (event as CustomEvent<{ view?: "project" | "agent"; projectID?: string; agent?: NavigationAgent }>).detail
      if (detail?.view === "agent" && detail.agent) {
        if (detail.projectID && detail.projectID !== store.selectedProjectID) selectProject(detail.projectID)
        localStorage.setItem("datox.agent.centralView", "agent")
        localStorage.setItem("datox.agent.selectedAgent", JSON.stringify(detail.agent))
        setStore({ centralView: "agent", selectedAgent: detail.agent })
        return
      }
      if (detail?.projectID) {
        localStorage.setItem("datox.agent.centralView", "project")
        localStorage.removeItem("datox.agent.selectedAgent")
        setStore({ centralView: "project", selectedAgent: undefined })
        selectProject(detail.projectID)
      }
    }
    const addProjectListener = () => addProject()
    const editProjectListener = (event: Event) => {
      const detail = (event as CustomEvent<{ projectID?: string }>).detail
      const project = localProjects().find((item) => item.worktree === detail?.projectID)
      if (project) editProject(project)
    }
    const deleteProjectListener = (event: Event) => {
      const detail = (event as CustomEvent<{ projectID?: string }>).detail
      if (detail?.projectID) removeProject(detail.projectID)
    }
    const periodListener = (event: Event) => {
      const detail = (event as CustomEvent<{ projectID?: string; periodID?: string }>).detail
      if (!detail?.periodID) return
      if (detail.periodID === store.selectedPeriodID) return
      if (detail.projectID && detail.projectID !== store.selectedProjectID) selectProject(detail.projectID)
      selectPeriod(detail.periodID, { notify: false })
    }
    const addPeriodListener = (event: Event) => {
      const detail = (event as CustomEvent<{ projectID?: string }>).detail
      if (detail?.projectID && detail.projectID !== store.selectedProjectID) selectProject(detail.projectID)
      addPeriod()
    }
    const deletePeriodListener = (event: Event) => {
      const detail = (event as CustomEvent<{ projectID?: string; periodID?: string }>).detail
      if (!detail?.projectID || !detail.periodID) return
      void deletePeriod(detail.projectID, detail.periodID).then(() => {
        void listPeriods(detail.projectID!).then((periods) => {
          setStore("periods", periods)
          const periodID = latestPeriod(periods)?.id || ""
          setStore("selectedPeriodID", periodID)
          rememberSelectedPeriod(periodID)
          window.dispatchEvent(new CustomEvent("datox:periods-changed"))
          void listSessions(detail.projectID!, periodID || undefined).then((sessions) => setStore("sessions", sessions))
        })
      })
    }
    window.addEventListener("datox:navigation-toggle", navigationListener)
    window.addEventListener("datox:navigation-select", selectionListener)
    window.addEventListener("datox:project-add", addProjectListener)
    window.addEventListener("datox:project-edit", editProjectListener)
    window.addEventListener("datox:project-delete", deleteProjectListener)
    window.addEventListener("datox:period-select", periodListener)
    window.addEventListener("datox:period-add", addPeriodListener)
    window.addEventListener("datox:period-delete", deletePeriodListener)
    onCleanup(() => {
      window.removeEventListener("datox:navigation-toggle", navigationListener)
      window.removeEventListener("datox:navigation-select", selectionListener)
      window.removeEventListener("datox:project-add", addProjectListener)
      window.removeEventListener("datox:project-edit", editProjectListener)
      window.removeEventListener("datox:project-delete", deleteProjectListener)
      window.removeEventListener("datox:period-select", periodListener)
      window.removeEventListener("datox:period-add", addPeriodListener)
      window.removeEventListener("datox:period-delete", deletePeriodListener)
    })
  })

  const applyStoredNavigationSelection = () => {
    const view = storedCentralView()
    if (view === "agent") {
      const agent = storedSelectedAgent()
      if (agent) setStore({ centralView: "agent", selectedAgent: agent })
      return
    }
    const projectID = storedSelectedProject()
    if (!projectID) return
    if (!projects().some((project) => project.id === projectID)) {
      rememberSelectedProject("")
      localStorage.removeItem("datox.agent.selectedAgent")
      setStore({ centralView: "project", selectedAgent: undefined })
      return
    }
    setStore({ centralView: "project", selectedAgent: undefined })
    selectProject(projectID)
  }

  const selectProject = (projectID: string, options: { allowMissing?: boolean } = {}) => {
    const next = projectID
    if (!options.allowMissing && next && projects().length > 0 && !projects().some((project) => project.id === next)) {
      rememberSelectedProject("")
      setStore({ selectedProjectID: "", selectedSessionID: "", selectedPeriodID: "", sessions: [], periods: [] })
      return
    }
    rememberSelectedProject(next)
    setStore({ selectedProjectID: next, selectedSessionID: "", selectedPeriodID: "", sessions: [], periods: [] })
    if (!next) return
    void listPeriods(next).then((periods) => {
      setStore("periods", periods)
      const stored = storedSelectedPeriod()
      const periodID = periods.find((period) => period.id === stored)?.id || latestPeriod(periods)?.id || ""
      setStore("selectedPeriodID", periodID)
      if (periodID) rememberSelectedPeriod(periodID)
      if (periodID) window.dispatchEvent(new CustomEvent("datox:period-select", { detail: { projectID: next, periodID } }))
      void listSessions(next, periodID || undefined).then((sessions) => setStore("sessions", sessions))
    }).catch(() => {
      if (store.selectedProjectID !== next) return
      rememberSelectedProject("")
      setStore({ selectedProjectID: "", selectedSessionID: "", selectedPeriodID: "", sessions: [], periods: [] })
    })
  }

  const selectPeriod = (periodID: string, options: { notify?: boolean } = {}) => {
    setStore("selectedPeriodID", periodID)
    rememberSelectedPeriod(periodID)
    if (options.notify !== false) {
      window.dispatchEvent(new CustomEvent("datox:period-select", { detail: { projectID: store.selectedProjectID, periodID } }))
    }
    if (!store.selectedProjectID) return
    void listSessions(store.selectedProjectID, periodID || undefined).then((sessions) => setStore("sessions", sessions))
  }

  const addProject = () => {
    dialog.show(() => (
      <DialogDatoxProjectForm
        title={language.t("datox.project.createTitle")}
        submitLabel={language.t("datox.projects.create")}
        onSubmit={(name) => {
          void createProject(name).then((project) => {
            void projectActions.refetch()
            window.dispatchEvent(new CustomEvent("datox:projects-changed"))
            selectProject(project.id, { allowMissing: true })
            localStorage.setItem("datox.agent.centralView", "project")
            localStorage.removeItem("datox.agent.selectedAgent")
            window.dispatchEvent(new CustomEvent("datox:navigation-select", { detail: { view: "project", projectID: project.id } }))
          })
        }}
      />
    ))
  }

  const editProject = (project: LocalProject) => {
    dialog.show(() => (
      <DialogDatoxProjectForm
        title={language.t("dialog.project.edit.title")}
        initialName={project.name ?? project.worktree}
        submitLabel={language.t("common.save")}
        onSubmit={(name) =>
          void updateProject(project.worktree, name).then(() => {
            void projectActions.refetch()
            window.dispatchEvent(new CustomEvent("datox:projects-changed"))
          })
        }
      />
    ))
  }

  const removeProject = (projectID: string) => {
    const project = projects().find((item) => item.id === projectID)
    dialog.show(() => (
      <DialogDatoxDeleteProject
        name={project?.name ?? projectID}
        onDelete={() => {
          void deleteProject(projectID).then(() => {
            if (store.selectedProjectID === projectID)
              setStore({ selectedProjectID: "", selectedSessionID: "", selectedPeriodID: "", sessions: [], periods: [] })
            void projectActions.refetch()
            window.dispatchEvent(new CustomEvent("datox:projects-changed"))
          })
        }}
      />
    ))
  }

  const newSession = () => {
    const project = selectedProject() ?? projects()[0]
    if (!project || project.project_role === "viewer") return
    const period = store.periods.find((item) => item.id === store.selectedPeriodID)
    if (!period) {
      showToast({
        title: language.t("datox.newChat.periodRequired.title"),
        description: language.t("datox.newChat.periodRequired.description"),
      })
      return
    }
    void createSession(project.id, `${project.name} - ${period.name}`, period.id).then((session) => {
      if (store.selectedProjectID !== project.id) setStore("selectedProjectID", project.id)
      setStore("sessions", (sessions) => [session, ...sessions])
      setStore("selectedSessionID", session.id)
      void openDatoxSession(session)
    })
  }

  const addPeriod = () => {
    const project = selectedProject() ?? projects()[0]
    if (!project || project.project_role === "viewer") return
    if (store.selectedProjectID !== project.id) selectProject(project.id)
    dialog.show(() => (
      <DialogDatoxPeriodForm
        onSubmit={(value) => {
          void createPeriod(project.id, value.name, value.metadata).then((period) => {
            setStore("periods", (periods) => [period, ...periods])
            selectPeriod(period.id)
            window.dispatchEvent(new CustomEvent("datox:periods-changed"))
          })
        }}
      />
    ))
  }

  const toggleNavigation = () => {
    setStore("navigationCollapsed", (value) => {
      const next = !value
      rememberNavigationCollapsed(next)
      return next
    })
  }

  const openDatoxSession = async (session: DatoxSession | Session) => {
    const projectID = "project_id" in session ? session.project_id : session.directory
    const remote = await ensureRemoteSession(projectID, session.id)
    if (!remote.remote_opencode_session_id) return
    const conn = ensureOpenCodeServer()
    navigate(sessionHref(ServerConnection.key(conn), remote.remote_opencode_session_id))
  }

  const deleteSession = async (session: Session) => {
    dialog.show(() => (
      <DialogDatoxDeleteSession
        name={session.title}
        onDelete={() => {
          void deleteDatoxSession(session.directory, session.id).then(() => {
            setStore("sessions", (sessions) => sessions.filter((item) => item.id !== session.id))
            if (store.selectedSessionID === session.id) setStore("selectedSessionID", "")
          })
        }}
      />
    ))
  }

  const ensureOpenCodeServer = () => {
    const url = import.meta.env.VITE_DATOX_OPENCODE_SERVER_URL || ""
    const username = import.meta.env.VITE_DATOX_OPENCODE_USERNAME || "opencode"
    const password = import.meta.env.VITE_DATOX_OPENCODE_PASSWORD || ""
    const existing = server.list.find((item) => item.type === "http" && item.http.url.replace(/\/+$/, "") === url.replace(/\/+$/, ""))
    if (existing) return existing
    return server.add({ type: "http", http: { url, username, password }, displayName: "Datox Report Agent" })!
  }

  const logout = () => {
    dialog.show(() => (
      <DialogDatoxLogout
        user={profileDescription()}
        onLogout={() => {
          localStorage.removeItem("activeCompanyId")
          localStorage.removeItem("datox.agent.companyId")
          localStorage.removeItem("datox.agent.userId")
          localStorage.removeItem("datox.agent.userEmail")
          sessionStorage.clear()
          window.location.reload()
        }}
      />
    ))
  }

  return (
    <div
      class={`
        m-2 min-h-0 flex-1 self-stretch overflow-hidden rounded-[10px]
        bg-v2-background-bg-base shadow-[var(--v2-elevation-raised)]
      `}
    >
      <ScrollView
        class="h-full [container-type:size]"
        thumbContainer={scroll.viewport.thumbTrack}
        thumbHoverTarget={scroll.viewport.hoverTarget}
        viewportRef={scroll.viewport.setViewport}
        onScroll={(event) => scroll.viewport.update(event.currentTarget.scrollTop)}
        onWheel={scroll.viewport.containOuterWheel}
      >
        <div
          class={`
            mx-auto grid min-h-full w-full max-w-[760px] grid-rows-[auto_minmax(0,1fr)_auto] gap-4 px-3 lg:px-6
          `}
        >
          <div class="min-h-0 min-w-0 flex flex-col">
            <div class="mt-6 lg:mt-14" />
            <Show when={store.centralView === "agent" && store.selectedAgent} fallback={<HomeSessionsView
                language={language}
                groups={groups}
                showProjectName={() => !selectedLocalProject()}
                server={() => ServerConnection.key(datoxServer)}
                canCreateSession={() => !!(selectedProject() ?? projects()[0]) && (selectedProject() ?? projects()[0])?.project_role !== "viewer"}
                searchValue={() => store.search}
                searchPlaceholder={() => language.t("home.sessions.search.placeholder")}
                searchOpen={() => store.searchFocused && store.search.trim().length > 0}
                searchLoading={() => false}
                searchResults={filteredRecords}
                searchActive={() => filteredRecords()[0] ? `${filteredRecords()[0].session.directory}:${filteredRecords()[0].session.id}` : ""}
                searchNoResultsLabel={() => language.t("home.sessions.search.noResults", { query: store.search })}
                titleOpacity={() => 1}
                isOpenTab={(record) => record.session.id === store.selectedSessionID}
                onCreateSession={newSession}
                onOpenSession={(session) => void openDatoxSession(session)}
                onArchiveSession={deleteSession}
                showArchiveSession={() => true}
                archiveSessionLabel={() => language.t("common.delete")}
                onSetHoverTarget={scroll.viewport.setHoverTarget}
                onSetThumbTrack={scroll.viewport.setThumbTrack}
                onSetContent={scroll.header.setContent}
                onSetHeader={scroll.header.setHeader}
                onWheel={scroll.viewport.containWheel}
                onSetSearchRoot={() => {}}
                onSetSearchInput={() => {}}
                onSetSearchList={() => {}}
                onSearchFocus={() => setStore("searchFocused", true)}
                onSearchInput={(value) => setStore("search", value)}
                onSearchClose={() => setStore({ search: "", searchFocused: false })}
                onSearchMove={() => {}}
                onSearchSelectActive={() => {
                  const record = filteredRecords()[0]
                  if (record) setStore("selectedSessionID", record.session.id)
                }}
                onSearchHighlight={() => {}}
                onSearchSelect={(record, _options?: OpenSessionOptions) => setStore("selectedSessionID", record.session.id)}
              />}
            >
              {(agent) => <DatoxAgentConfigView agent={agent()} projectID={store.selectedProjectID} language={language} onClose={() => setStore({ centralView: "project", selectedAgent: undefined })} />}
            </Show>
          </div>
        </div>
      </ScrollView>
    </div>
  )
}

function datoxLanguage(language: ReturnType<typeof useLanguage>) {
  const labels: Record<string, string> = {
    "command.session.new": "New chat",
    "home.sessions.search.placeholder": "Search chats",
    "home.sessions.search.placeholder.scoped": "Search chats in {{scope}}",
    "home.sessions.search.sessions": "Chats",
    "home.sessions.search.noResults": "No chats found for {{query}}",
    "home.sessions.empty.description": "Create a chat to get started",
    "sidebar.project.recentSessions": "Recent chats",
    "common.close": "Delete",
    "dialog.project.edit.title": "Edit name",
  }
  return {
    ...language,
    t: (key: string, params?: Record<string, string | number | boolean>) => {
      const label = labels[key]
      if (!label) return language.t(key, params)
      return Object.entries(params ?? {}).reduce(
        (value, [param, replacement]) => value.replaceAll(`{{${param}}}`, String(replacement)),
        label,
      )
    },
  } as ReturnType<typeof useLanguage>
}

function toLocalProject(project: DatoxProject): LocalProject {
  return {
    id: project.id,
    name: project.name,
    worktree: project.id,
    expanded: true,
  }
}

function latestProject(projects: DatoxProject[]) {
  return projects.toSorted((a, b) => projectCreatedTime(b) - projectCreatedTime(a))[0]
}

function projectCreatedTime(project: DatoxProject) {
  return Date.parse(project.created_at) || Date.parse(project.updated_at) || 0
}

function rememberSelectedProject(projectID: string) {
  try {
    if (projectID) {
      localStorage.setItem("datox.agent.selectedProjectId", projectID)
      return
    }
    localStorage.removeItem("datox.agent.selectedProjectId")
  } catch {
    return
  }
}

function storedNavigationCollapsed() {
  try {
    return localStorage.getItem("datox.agent.navigationCollapsed") === "true"
  } catch {
    return false
  }
}

function storedSelectedProject() {
  try {
    return localStorage.getItem("datox.agent.selectedProjectId") || ""
  } catch {
    return ""
  }
}

function storedCentralView(): "project" | "agent" {
  try {
    return localStorage.getItem("datox.agent.centralView") === "agent" ? "agent" : "project"
  } catch {
    return "project"
  }
}

function storedSelectedAgent() {
  try {
    const value = localStorage.getItem("datox.agent.selectedAgent")
    return value ? (JSON.parse(value) as NavigationAgent) : undefined
  } catch {
    return undefined
  }
}

function rememberNavigationCollapsed(value: boolean) {
  try {
    localStorage.setItem("datox.agent.navigationCollapsed", String(value))
  } catch {
    return
  }
}

function storedSelectedPeriod() {
  try {
    return localStorage.getItem("datox.agent.selectedPeriodId") || ""
  } catch {
    return ""
  }
}

function rememberSelectedPeriod(periodID: string) {
  try {
    if (periodID) {
      localStorage.setItem("datox.agent.selectedPeriodId", periodID)
      return
    }
    localStorage.removeItem("datox.agent.selectedPeriodId")
  } catch {
    return
  }
}

function DatoxCollapsedNavigation(props: {
  language: ReturnType<typeof useLanguage>
  projects: DatoxProject[]
  selectedProjectID: string
  onToggle: () => void
  onSelectProject: (projectID: string) => void
  onAddProject: () => void
}) {
  return (
    <aside class="mt-6 flex min-h-0 min-w-0 flex-col items-center gap-3 overflow-hidden lg:sticky lg:top-14 lg:mt-14 lg:h-[calc(100cqh-56px)] lg:self-start lg:pt-[52px]">
      <TooltipV2 placement="right" value={props.language.t("datox.navigation.expand")}>
        <IconButtonV2
          variant="ghost-muted"
          size="large"
          icon={<IconV2 name="sidebar-right" />}
          onClick={props.onToggle}
          aria-label={props.language.t("datox.navigation.expand")}
        />
      </TooltipV2>
      <TooltipV2 placement="right" value={props.language.t("home.project.add")}>
        <IconButtonV2
          data-action="home-add-project"
          variant="ghost-muted"
          size="large"
          icon={<IconV2 name="plus" />}
          onClick={props.onAddProject}
          aria-label={props.language.t("home.project.add")}
        />
      </TooltipV2>
      <div class="h-px w-9 bg-v2-border-border-base" />
      <div class="flex min-h-0 flex-col items-center gap-2 overflow-y-auto py-1">
        <For each={props.projects}>
          {(project) => (
            <TooltipV2 placement="right" value={project.name}>
              <button
                type="button"
                class="flex size-8 shrink-0 items-center justify-center rounded-lg border border-v2-border-border-base text-13-medium text-v2-text-text-muted hover:bg-v2-overlay-simple-overlay-hover hover:text-v2-text-text-base"
                classList={{ "bg-v2-background-bg-layer-03 text-v2-text-text-base": props.selectedProjectID === project.id }}
                onClick={() => props.onSelectProject(project.id)}
                aria-label={project.name}
              >
                {project.name.trim().charAt(0).toUpperCase() || "P"}
              </button>
            </TooltipV2>
          )}
        </For>
      </div>
      <div class="h-px w-9 bg-v2-border-border-base" />
      <TooltipV2 placement="right" value={props.language.t("datox.agents.title")}>
        <button
          type="button"
          class="flex size-8 shrink-0 items-center justify-center rounded-lg border border-v2-border-border-base text-13-medium text-v2-text-text-muted hover:bg-v2-overlay-simple-overlay-hover hover:text-v2-text-text-base"
          onClick={props.onToggle}
          aria-label={props.language.t("datox.agents.title")}
        >
          A
        </button>
      </TooltipV2>
    </aside>
  )
}

function DatoxAgentConfigView(props: { agent: NavigationAgent; projectID: string; language: ReturnType<typeof useLanguage>; onClose: () => void }) {
  const builtIn = () => ["custom-report", "corporate-onboarding"].includes(props.agent.id)
  const draft = () => props.agent.id.startsWith("draft-")
  const [form, setForm] = createStore({
    foundationID: "custom-report",
    name: props.agent.name,
    description: props.agent.description ?? "",
    instructions: props.agent.instructions ?? "",
    mode: props.agent.mode ?? "primary",
    enabled: props.agent.enabled !== false,
    tools: props.agent.tools ?? {},
    integrations: props.agent.integrations ?? [],
    permissions: props.agent.permissions ?? {},
  })
  const applyFoundation = (foundationID: string) => {
    const foundation = foundationAgents().find((agent) => agent.id === foundationID)
    if (!foundation) return
    setForm({
      foundationID,
      name: `New ${foundation.name}`,
      description: foundation.description ?? "",
      instructions: foundation.instructions ?? "",
      mode: foundation.mode ?? "primary",
      enabled: true,
      tools: foundation.tools ?? {},
      integrations: foundation.integrations ?? [],
      permissions: foundation.permissions ?? {},
    })
  }
  const save = () => {
    if (!props.projectID || builtIn() || !form.name.trim()) return
    const agents = loadStoredAgents(props.projectID)
    const nextAgent = {
      ...props.agent,
      id: props.agent.id.startsWith("draft-") ? `agent-${Date.now()}` : props.agent.id,
      name: form.name.trim(),
      description: form.description.trim(),
      instructions: form.instructions.trim(),
      mode: form.mode,
      enabled: form.enabled,
      tools: form.tools,
      integrations: form.integrations,
      permissions: form.permissions,
      updatedAt: new Date().toISOString(),
    }
    const next = agents.some((agent) => agent.id === nextAgent.id)
      ? agents.map((agent) => (agent.id === nextAgent.id ? nextAgent : agent))
      : [...agents, nextAgent]
    saveStoredAgents(props.projectID, next)
    localStorage.setItem("datox.agent.selectedAgent", JSON.stringify(nextAgent))
    window.dispatchEvent(new CustomEvent("datox:agents-changed"))
  }
  const remove = () => {
    if (!props.projectID || !props.agent.id || builtIn()) return
    saveStoredAgents(props.projectID, loadStoredAgents(props.projectID).filter((agent) => agent.id !== props.agent.id))
    localStorage.setItem("datox.agent.centralView", "project")
    localStorage.removeItem("datox.agent.selectedAgent")
    window.dispatchEvent(new CustomEvent("datox:agents-changed"))
    props.onClose()
  }
  return (
    <section class="min-h-0 min-w-0 flex-1 overflow-auto px-3 py-6 lg:px-0 lg:py-12">
      <div class="rounded-xl border border-v2-border-border-base bg-v2-background-bg-layer-01 p-5">
        <div class="mb-5 flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="mb-2 flex flex-wrap items-center gap-2">
              <Show
                when={!builtIn()}
                fallback={<h1 class="truncate text-22-semibold text-v2-text-text-base">{props.agent.name}</h1>}
              >
                <input
                  class="h-10 min-w-[260px] rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 text-20-semibold text-v2-text-text-base outline-none"
                  value={form.name}
                  placeholder={props.language.t("datox.agents.name")}
                  onInput={(event) => setForm("name", event.currentTarget.value)}
                />
              </Show>
              <Show when={props.agent.isDefault}>
                <span class="rounded-full border border-blue-500/30 bg-blue-500/10 px-2 py-0.5 text-11-medium text-blue-300">{props.language.t("datox.agents.default")}</span>
              </Show>
              <Show when={builtIn()}>
                <span class="rounded-full border border-v2-border-border-base px-2 py-0.5 text-11-medium text-v2-text-text-muted">{props.language.t("datox.agents.builtIn")}</span>
              </Show>
              <span class="rounded-full border border-v2-border-border-base px-2 py-0.5 text-11-medium text-v2-text-text-muted">
                {props.language.t(props.agent.enabled === false ? "datox.agents.disabled" : "datox.agents.enabled")}
              </span>
            </div>
            <Show
              when={!builtIn()}
              fallback={<p class="text-13-regular text-v2-text-text-muted">{props.agent.description || props.language.t("datox.agents.noDescription")}</p>}
            >
              <input
                class="mt-2 h-9 w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 text-13-regular text-v2-text-text-muted outline-none"
                value={form.description}
                placeholder={props.language.t("datox.agents.shortDescription")}
                onInput={(event) => setForm("description", event.currentTarget.value)}
              />
            </Show>
          </div>
          <span class="rounded-md border border-v2-border-border-base px-2 py-1 text-12-medium text-v2-text-text-muted">
            {props.language.t(`datox.agents.mode.${props.agent.mode || "primary"}`)}
          </span>
          <Show when={!builtIn()}>
            <div class="flex gap-2">
              <button class="rounded-md border border-v2-border-border-base px-3 py-1.5 text-12-medium text-v2-text-text-muted hover:bg-v2-background-bg-layer-02 hover:text-v2-text-text-base" onClick={remove}>
                {props.language.t("common.delete")}
              </button>
              <button class="rounded-md bg-v2-button-primary-bg px-3 py-1.5 text-12-medium text-v2-button-primary-text disabled:opacity-50" disabled={!form.name.trim()} onClick={save}>
                {props.language.t("common.save")}
              </button>
            </div>
          </Show>
        </div>

        <div class="grid gap-4">
          <Show when={draft()}>
            <AgentConfigSection title={props.language.t("datox.agents.foundation")}> 
              <select
                class="h-10 w-full rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 text-13-regular text-v2-text-text-base outline-none"
                value={form.foundationID}
                onChange={(event) => applyFoundation(event.currentTarget.value)}
              >
                <For each={foundationAgents()}>{(agent) => <option value={agent.id}>{agent.name}</option>}</For>
              </select>
            </AgentConfigSection>
          </Show>
          <AgentConfigSection title={props.language.t("datox.agents.instructions")}>
            <Show
              when={!builtIn()}
              fallback={
                <pre class="max-h-72 overflow-auto whitespace-pre-wrap rounded-lg bg-v2-background-bg-base p-4 text-13-regular leading-6 text-v2-text-text-base">
                  {props.agent.instructions || props.language.t("datox.agents.noInstructions")}
                </pre>
              }
            >
              <textarea
                class="min-h-48 w-full rounded-lg border border-v2-border-border-base bg-v2-background-bg-base p-4 text-13-regular leading-6 text-v2-text-text-base outline-none"
                value={form.instructions}
                placeholder={props.language.t("datox.agents.instructions")}
                onInput={(event) => setForm("instructions", event.currentTarget.value)}
              />
            </Show>
          </AgentConfigSection>
          <AgentConfigSection title={props.language.t("datox.agents.tools")}>
            <PillList items={Object.entries(builtIn() ? props.agent.tools ?? {} : form.tools).filter(([, enabled]) => enabled).map(([tool]) => props.language.t(`datox.agents.tool.${tool}`))} empty={props.language.t("datox.agents.noneEnabled")} />
          </AgentConfigSection>
          <AgentConfigSection title={props.language.t("datox.agents.integrations")}>
            <PillList items={(builtIn() ? props.agent.integrations ?? [] : form.integrations).map((item) => props.language.t(`datox.integrations.${item}`))} empty={props.language.t("datox.agents.noneEnabled")} />
          </AgentConfigSection>
          <AgentConfigSection title={props.language.t("datox.agents.permissions")}>
            <div class="grid gap-2 sm:grid-cols-2">
              <For each={Object.entries(builtIn() ? props.agent.permissions ?? {} : form.permissions)}>
                {([permission, value]) => (
                  <div class="flex items-center justify-between rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 py-2 text-12-regular">
                    <span class="text-v2-text-text-muted">{props.language.t(`datox.agents.permission.${permission}`)}</span>
                    <span class="text-v2-text-text-base">{props.language.t(`datox.agents.permission.${value}`)}</span>
                  </div>
                )}
              </For>
            </div>
          </AgentConfigSection>
        </div>
      </div>
    </section>
  )
}

function loadStoredAgents(projectID: string): NavigationAgent[] {
  try {
    return JSON.parse(localStorage.getItem(`datox.agent.projectAgents.${projectID}`) || "[]") as NavigationAgent[]
  } catch {
    return []
  }
}

function saveStoredAgents(projectID: string, agents: NavigationAgent[]) {
  localStorage.setItem(`datox.agent.projectAgents.${projectID}`, JSON.stringify(agents))
}

function foundationAgents(): NavigationAgent[] {
  return [
    {
      id: "custom-report",
      name: "Custom Report Agent",
      description: "Extracts facts, validates inputs, and drafts custom reports from project documents.",
      instructions: "Help users build compliance, risk, audit, and management reports from project documents. Extract relevant facts, validate inputs, identify missing information, cite available sources, and draft clear report outputs with assumptions and open issues.",
      mode: "primary",
      enabled: true,
      tools: { readFiles: true, generateReports: true, webSearch: false, approvalWorkflow: true, integrations: true, calculations: true },
      integrations: ["google-drive", "one-drive", "sharepoint-online"],
      permissions: { read: "allow", edit: "ask", websearch: "deny", webfetch: "ask", bash: "deny", skill: "allow" },
    },
    {
      id: "corporate-onboarding",
      name: "Corporate Onboarding Agent",
      description: "Orchestrates corporate onboarding workflows across KYC documents, checks, approvals, and audit trail.",
      instructions: "Process onboarding requests by extracting company facts, validating KYC completeness, checking risk and sanctions requirements, requesting missing data, preparing approval packages, proposing system updates, and summarizing the audit trail.",
      mode: "primary",
      enabled: true,
      tools: { readFiles: true, generateReports: true, webSearch: true, approvalWorkflow: true, integrations: true, calculations: true },
      integrations: ["google-drive", "one-drive", "sharepoint-online"],
      permissions: { read: "allow", edit: "ask", websearch: "allow", webfetch: "ask", bash: "deny", skill: "allow" },
    },
  ]
}

function AgentConfigSection(props: { title: string; children: JSX.Element }) {
  return (
    <div>
      <h2 class="mb-2 text-14-medium text-v2-text-text-base">{props.title}</h2>
      {props.children}
    </div>
  )
}

function PillList(props: { items: string[]; empty: string }) {
  return (
    <Show when={props.items.length > 0} fallback={<p class="text-13-regular text-v2-text-text-muted">{props.empty}</p>}>
      <div class="flex flex-wrap gap-2">
        <For each={props.items}>{(item) => <span class="rounded-md bg-v2-background-bg-base px-2 py-1 text-12-regular text-v2-text-text-muted">{item}</span>}</For>
      </div>
    </Show>
  )
}

function latestPeriod(periods: DatoxPeriod[]) {
  return periods.toSorted((a, b) => periodCreatedTime(b) - periodCreatedTime(a))[0]
}

function periodCreatedTime(period: DatoxPeriod) {
  return Date.parse(period.created_at) || Date.parse(period.updated_at) || 0
}

function toSession(session: DatoxSession): Session {
  return {
    id: session.id,
    directory: session.project_id,
    title: session.title,
    time: {
      created: Date.parse(session.created_at),
      updated: Date.parse(session.updated_at),
    },
  } as Session
}

function buildRecords(sessions: DatoxSession[], projects: LocalProject[]): HomeSessionRecord[] {
  const projectByID = new Map(projects.map((project) => [project.worktree, project]))
  return sessions.flatMap((session) => {
    const project = projectByID.get(session.project_id)
    if (!project) return []
    return [{ session: toSession(session), project, projectName: project.name ?? project.worktree }]
  })
}

function groupSessions(records: HomeSessionRecord[], language: ReturnType<typeof useLanguage>): HomeSessionGroup[] {
  const now = DateTime.local()
  const yesterday = now.minus({ days: 1 })
  const today = records.filter((record) => DateTime.fromMillis(record.session.time.updated ?? record.session.time.created).hasSame(now, "day"))
  const yesterdayRecords = records.filter((record) =>
    DateTime.fromMillis(record.session.time.updated ?? record.session.time.created).hasSame(yesterday, "day"),
  )
  const older = records.filter((record) => {
    const time = DateTime.fromMillis(record.session.time.updated ?? record.session.time.created)
    return !time.hasSame(now, "day") && !time.hasSame(yesterday, "day")
  })
  return [
    { id: "today" as const, title: language.t("home.sessions.group.today"), sessions: today },
    { id: "yesterday" as const, title: language.t("home.sessions.group.yesterday"), sessions: yesterdayRecords },
    { id: "older" as const, title: language.t("home.sessions.group.older"), sessions: older },
  ].filter((group) => group.sessions.length > 0)
}
